import React, { useState, useEffect } from 'react'
import { Table, Button, Card, Space, DatePicker, Select, Typography, Tag } from 'antd'
import dayjs from 'dayjs'
import receptionApi from '../../api/reception'
import type { ReceptionRecord } from '../../types'
import { DownloadOutlined } from '@ant-design/icons'

const { Title } = Typography
const { RangePicker } = DatePicker

const Receptions: React.FC = () => {
  const [records, setRecords] = useState<ReceptionRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState<any>(null)
  const [caseType, setCaseType] = useState<string | undefined>()

  useEffect(() => {
    loadRecords()
  }, [dateRange, caseType])

  const loadRecords = async () => {
    setLoading(true)
    try {
      const params: any = { skip: 0, limit: 100 }
      if (dateRange && dateRange.length === 2) {
        params.start_date = dateRange[0].format('YYYY-MM-DD')
        params.end_date = dateRange[1].format('YYYY-MM-DD')
      }
      if (caseType) {
        params.case_type = caseType
      }
      const data = await receptionApi.getList(params.skip, params.limit, params)
      setRecords(data)
    } catch (error) {
      console.error('Failed to load receptions:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    try {
      const params: any = {}
      if (dateRange && dateRange.length === 2) {
        params.start_date = dateRange[0].format('YYYY-MM-DD')
        params.end_date = dateRange[1].format('YYYY-MM-DD')
      }
      if (caseType) {
        params.case_type = caseType
      }
      const blob = await receptionApi.export(params)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `接访记录_${dayjs().format('YYYYMMDD')}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '案件类型',
      dataIndex: 'case_type',
      key: 'case_type',
      render: (type: string) => {
        const colors: any = {
          civil: 'blue',
          criminal: 'red',
          administrative: 'green',
          execution: 'orange',
        }
        return <Tag color={colors[type]}>{type}</Tag>
      },
    },
    {
      title: '诉讼阶段',
      dataIndex: 'current_stage',
      key: 'current_stage',
    },
    {
      title: '裁判结果',
      dataIndex: 'stage_result',
      key: 'stage_result',
    },
    {
      title: '指导结果',
      dataIndex: 'guidance_text',
      key: 'guidance_text',
      ellipsis: true,
    },
    {
      title: '接访时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
  ]

  return (
    <div>
      <Card style={{ marginBottom: 20 }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Space>
            <Title level={4} style={{ margin: 0 }}>接访记录管理</Title>
          </Space>
          <Space>
            <RangePicker
              onChange={setDateRange}
              allowClear={false}
            />
            <Select
              placeholder="案件类型"
              allowClear
              style={{ width: 150 }}
              onChange={setCaseType}
              options={[
                { value: 'civil', label: '民事' },
                { value: 'criminal', label: '刑事' },
                { value: 'administrative', label: '行政' },
                { value: 'execution', label: '执行' },
              ]}
            />
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              导出 Excel
            </Button>
          </Space>
        </Space>
      </Card>

      <Table
        columns={columns}
        dataSource={records}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 20 }}
      />
    </div>
  )
}

export default Receptions
